import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Database, Server, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';

const statusIcon = (status) => {
  if (status === 'ok') return CheckCircle;
  if (status === 'fail') return AlertTriangle;
  return Server;
};

const statusColor = (status) => {
  if (status === 'ok') return 'text-green-600';
  if (status === 'fail') return 'text-red-600';
  return 'text-gray-500';
};

const InfrastructurePanel = ({ infrastructure, onRefresh, isLoading }) => {
  if (!infrastructure && !isLoading) {
    return null;
  }

  const health = infrastructure?.health || {};
  const queue = infrastructure?.queue || {};
  const outbox = infrastructure?.outbox || {};
  const stripe = infrastructure?.stripe_webhooks || {};
  const horizon = infrastructure?.horizon || {};
  const search = infrastructure?.search || {};
  const drivers = infrastructure?.drivers || {};
  const alerts = infrastructure?.alerts || {};
  const overall = infrastructure?.status || 'unknown';
  const hasFailures =
    (queue.failed_jobs ?? 0) > 0 ||
    (outbox.failed ?? 0) > 0 ||
    (stripe.failed_24h ?? 0) > 0;

  return (
    <div className="bg-white border rounded-lg p-3 sm:p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Platform infrastructure
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Live checks from Redis, queue, outbox, and Stripe webhooks
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs font-semibold uppercase px-2.5 py-1 rounded-full ${
              overall === 'ok' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
            }`}
          >
            {overall}
          </span>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              aria-label="Refresh infrastructure"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Core services
          </h3>
          <div className="space-y-2">
            {Object.entries(health).map(([name, status]) => {
              const Icon = statusIcon(status);
              return (
                <div key={name} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-gray-600">{name}</span>
                  <span className={`inline-flex items-center gap-1 font-medium ${statusColor(status)}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {status}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
            {Object.entries(drivers).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="capitalize">{key}</span>
                <span className="font-mono">{value}</span>
              </div>
            ))}
            {search.driver === 'meilisearch' && (
              <div className="flex justify-between text-sm mt-3 pt-3 border-t border-gray-100">
                <span className="text-gray-600">Meilisearch</span>
                <span className={`inline-flex items-center gap-1 font-medium ${statusColor(search.status)}`}>
                  {search.status}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Queues & outbox</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Pending jobs</span>
              <span className="font-semibold tabular-nums">{queue.pending_jobs ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Failed jobs</span>
              <span className={`font-semibold tabular-nums ${(queue.failed_jobs ?? 0) > 0 ? 'text-red-600' : ''}`}>
                {queue.failed_jobs ?? 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Outbox pending</span>
              <span className="font-semibold tabular-nums">{outbox.pending ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Outbox failed</span>
              <span className={`font-semibold tabular-nums ${(outbox.failed ?? 0) > 0 ? 'text-red-600' : ''}`}>
                {outbox.failed ?? 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Published today</span>
              <span className="font-semibold tabular-nums">{outbox.published_today ?? 0}</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Stripe webhooks (24h)</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Received</span>
              <span className="font-semibold tabular-nums">{stripe.received_24h ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Processed</span>
              <span className="font-semibold tabular-nums">{stripe.processed_24h ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Failed</span>
              <span className={`font-semibold tabular-nums ${(stripe.failed_24h ?? 0) > 0 ? 'text-red-600' : ''}`}>
                {stripe.failed_24h ?? 0}
              </span>
            </div>
          </div>
          {(stripe.failed_24h ?? 0) > 0 && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
              Failed webhooks can be retried from{' '}
              <Link to="/admin/failed-operations" className="font-medium underline">
                Failed operations
              </Link>
              {' '}or via <code className="font-mono">php artisan stripe:retry-failed-webhooks</code>.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Horizon queues</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Supervisors</span>
              <span className={`font-semibold ${horizon.running ? 'text-green-700' : 'text-gray-500'}`}>
                {horizon.enabled ? (horizon.running ? 'running' : 'stopped') : 'n/a'}
              </span>
            </div>
            {Object.entries(horizon.wait_times || {}).map(([queueName, seconds]) => {
              const threshold = horizon.waits?.[queueName] ?? 60;
              const isLong = threshold !== 0 && seconds > threshold;

              return (
                <div key={queueName} className="flex justify-between">
                  <span className="text-gray-600 font-mono text-xs">{queueName}</span>
                  <span className={`font-semibold tabular-nums ${isLong ? 'text-red-600' : ''}`}>
                    {Math.round(seconds)}s
                  </span>
                </div>
              );
            })}
            {Object.keys(horizon.wait_times || {}).length === 0 && (
              <p className="text-xs text-gray-500">No wait metrics (start Horizon workers).</p>
            )}
          </div>
          {horizon.alerts_enabled && (
            <p className="mt-3 text-xs text-gray-500">
              {horizon.failed_job_alerts_enabled
                ? 'Long-wait and failed-job alerts enabled via ops email/Slack.'
                : 'Long-wait alerts enabled via ops email/Slack.'}
            </p>
          )}
        </div>
      </div>

      {hasFailures && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Link
            to="/admin/failed-operations"
            className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
          >
            <AlertTriangle className="w-4 h-4" />
            Review and retry failed operations
          </Link>
        </div>
      )}

      {alerts.enabled && (
        <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600">
          <p className="font-medium text-gray-900">Automated alerts</p>
          <p className="mt-1">
            Status:{' '}
            <span className={alerts.status === 'warning' ? 'text-amber-700 font-medium' : 'text-green-700 font-medium'}>
              {alerts.status === 'warning' ? 'issues detected' : 'healthy'}
            </span>
            {(alerts.channels?.email || alerts.channels?.slack) && (
              <span className="text-gray-500">
                {' '}via {[alerts.channels?.email && 'email', alerts.channels?.slack && 'Slack'].filter(Boolean).join(' + ')}
              </span>
            )}
          </p>
          {alerts.issues?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-amber-800">
              {alerts.issues.map((issue) => (
                <li key={issue.code}>• {issue.message} ({issue.value})</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default InfrastructurePanel;
